// BookingManagePage — public reschedule/cancel page at /book/manage/:token.
// NO auth. The token is the appointment's random sourceRef, embedded in the
// confirmation email. Lets the booker pick a new time or cancel on their own.
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Check, Calendar, Clock, AlertCircle, ArrowLeft, X } from "lucide-react";
import { getManageBooking, rescheduleBooking, cancelBooking } from "../api.js";

const LANG = (typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("es")) ? "es" : "en";
const t = (es, en) => (LANG === "es" ? es : en);

export function BookingManagePage({ token }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("view"); // view | reschedule | cancelConfirm
  const [selDate, setSelDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [done, setDone] = useState(null); // { kind: 'rescheduled'|'cancelled', when? }

  function load() {
    setLoadError("");
    getManageBooking(token)
      .then((d) => { setData(d); setSelDate(d.days?.[0]?.date || ""); })
      .catch((err) => setLoadError(err.message || String(err)));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const tz = data?.timezone || "America/New_York";
  const dayFmt = useMemo(() => new Intl.DateTimeFormat(LANG === "es" ? "es-US" : "en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }), [tz]);
  const longFmt = useMemo(() => new Intl.DateTimeFormat(LANG === "es" ? "es-US" : "en-US", { timeZone: tz, weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }), [tz]);
  const timeFmt = useMemo(() => new Intl.DateTimeFormat(LANG === "es" ? "es-US" : "en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }), [tz]);

  const days = data?.days || [];
  const selDay = days.find((d) => d.date === selDate) || null;

  async function doReschedule(startUtc) {
    setBusy(true); setActionError("");
    try {
      const res = await rescheduleBooking(token, startUtc);
      setDone({ kind: "rescheduled", when: res.when });
    } catch (err) {
      const msg = String(err.message || err);
      if (/slot_unavailable|too_soon/.test(msg)) {
        setActionError(t("Ese horario ya no está disponible. Elige otro.", "That time is no longer available. Pick another."));
        load();
      } else setActionError(msg);
    } finally { setBusy(false); }
  }

  async function doCancel() {
    setBusy(true); setActionError("");
    try {
      await cancelBooking(token);
      setDone({ kind: "cancelled" });
    } catch (err) {
      setActionError(err.message || String(err));
    } finally { setBusy(false); }
  }

  const page = { minHeight: "100vh", background: "linear-gradient(160deg,#eef6ff,#f8fbff)", padding: "24px 14px", boxSizing: "border-box", fontFamily: "system-ui,Arial,sans-serif", color: "#1a2433" };
  const card = { maxWidth: 560, margin: "0 auto", background: "#fff", borderRadius: 18, boxShadow: "0 10px 40px rgba(10,85,140,.12)", overflow: "hidden" };
  const pad = { padding: "22px 24px" };
  const btn = (bg, fg = "#fff") => ({ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 18px", borderRadius: 11, border: "none", background: bg, color: fg, fontSize: 15, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 });

  if (loadError) {
    return <div style={page}><div style={{ ...card, ...pad, textAlign: "center" }}>
      <AlertCircle size={34} style={{ color: "#e11d48", marginBottom: 8 }} />
      <h2 style={{ margin: "6px 0" }}>{t("Enlace no válido", "Invalid link")}</h2>
      <p style={{ color: "#64748b", fontSize: 14 }}>{t("No encontramos esta cita. El enlace pudo expirar o la cita ya no existe.", "We couldn't find this booking. The link may have expired or the booking no longer exists.")}</p>
    </div></div>;
  }
  if (!data) {
    return <div style={page}><div style={{ ...card, ...pad, textAlign: "center", color: "#64748b" }}><Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}</div></div>;
  }

  if (done) {
    const cancelled = done.kind === "cancelled";
    return <div style={page}><div style={{ ...card, ...pad, textAlign: "center" }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: cancelled ? "#fee2e2" : "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
        {cancelled ? <X size={34} style={{ color: "#dc2626" }} /> : <Check size={34} style={{ color: "#16a34a" }} />}
      </div>
      <h2 style={{ margin: "4px 0" }}>{cancelled ? t("Cita cancelada", "Booking cancelled") : t("¡Cita reprogramada!", "Booking rescheduled!")}</h2>
      {!cancelled && <p style={{ fontSize: 16, fontWeight: 600, color: "#0a558c", margin: "10px 0" }}>{done.when}</p>}
      <p style={{ color: "#64748b", fontSize: 13.5, marginTop: 12 }}>
        {cancelled ? t("Te enviamos un correo de confirmación.", "We emailed you a confirmation.") : t("Te enviamos un correo con los nuevos detalles.", "We emailed you the new details.")}
      </p>
    </div></div>;
  }

  if (data.status === "cancelled") {
    return <div style={page}><div style={{ ...card, ...pad, textAlign: "center" }}>
      <AlertCircle size={30} style={{ color: "#f59e0b", marginBottom: 6 }} />
      <h2 style={{ margin: "6px 0" }}>{t("Esta cita está cancelada", "This booking is cancelled")}</h2>
      <p style={{ color: "#64748b", fontSize: 14 }}>{t("Si quieres agendar de nuevo, pídele a tu agente un nuevo enlace.", "To book again, ask your agent for a new link.")}</p>
    </div></div>;
  }

  const agent = data.agent || {};
  return (
    <div style={page}>
      <div style={card}>
        <div style={{ ...pad, background: "linear-gradient(135deg,#0a558c,#0891b2)", color: "#fff", textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>{t("Tu cita", "Your booking")}</div>
          <div style={{ fontSize: 13, opacity: .9, marginTop: 2 }}>{t("con", "with")} {agent.name}{agent.company ? ` · ${agent.company}` : ""}</div>
        </div>

        <div style={pad}>
          <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, padding: "14px", marginBottom: 16, textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#0a558c", fontWeight: 700, fontSize: 15.5 }}>
              <Calendar size={17} /> {longFmt.format(new Date(data.startUtc))}
            </div>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}><Clock size={12} style={{ verticalAlign: "-2px" }} /> {data.durationMinutes} min · {data.meetingTitle || t("Consulta", "Consultation")}</div>
          </div>

          {actionError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 9, padding: "10px 12px", fontSize: 13.5, marginBottom: 12 }}>{actionError}</div>
          )}

          {mode === "view" && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={() => { setMode("reschedule"); setActionError(""); }} style={{ ...btn("#0a558c"), flex: 1 }}>
                <Calendar size={16} /> {t("Reprogramar", "Reschedule")}
              </button>
              <button type="button" onClick={() => { setMode("cancelConfirm"); setActionError(""); }} style={{ ...btn("transparent", "#dc2626"), flex: 1, border: "1px solid #fecaca" }}>
                <X size={16} /> {t("Cancelar cita", "Cancel booking")}
              </button>
            </div>
          )}

          {mode === "cancelConfirm" && (
            <div>
              <p style={{ fontSize: 14.5, color: "#1a2433" }}>{t("¿Seguro que quieres cancelar esta cita?", "Are you sure you want to cancel this booking?")}</p>
              <div style={{ display: "flex", gap: 10 }}>
                <button type="button" onClick={doCancel} disabled={busy} style={{ ...btn("#dc2626"), flex: 1 }}>
                  {busy ? <Loader2 size={16} className="dkt-spin" /> : <X size={16} />} {t("Sí, cancelar", "Yes, cancel")}
                </button>
                <button type="button" onClick={() => setMode("view")} disabled={busy} style={{ ...btn("transparent", "#475569"), flex: 1, border: "1px solid #cbd5e1" }}>
                  {t("Volver", "Back")}
                </button>
              </div>
            </div>
          )}

          {mode === "reschedule" && (
            <div>
              <button type="button" onClick={() => { setMode("view"); setActionError(""); }} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#0891b2", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 10 }}>
                <ArrowLeft size={15} /> {t("Volver", "Back")}
              </button>
              <div style={{ color: "#0a558c", fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{t("Elige un nuevo horario", "Pick a new time")}</div>
              {days.length === 0 ? (
                <p style={{ color: "#64748b", fontSize: 14 }}>{t("No hay horarios disponibles por ahora.", "No times available right now.")}</p>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6, marginBottom: 14 }}>
                    {days.map((d) => {
                      const active = d.date === selDate;
                      return (
                        <button key={d.date} type="button" onClick={() => setSelDate(d.date)}
                          style={{ flex: "0 0 auto", padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, border: active ? "2px solid #0891b2" : "1px solid #cbd5e1", background: active ? "#ecfeff" : "#fff", color: active ? "#0a558c" : "#475569" }}>
                          {dayFmt.format(new Date(d.slots[0].startUtc))}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: 8 }}>
                    {(selDay?.slots || []).map((s) => (
                      <button key={s.startUtc} type="button" disabled={busy} onClick={() => doReschedule(s.startUtc)}
                        style={{ padding: "10px 6px", borderRadius: 10, border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0a558c", fontWeight: 700, fontSize: 14, cursor: busy ? "wait" : "pointer" }}>
                        {timeFmt.format(new Date(s.startUtc))}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
